import React, { useMemo } from 'react'
import { useErrorHandler } from 'react-error-boundary'
import { useParams } from 'react-router'
import noop from 'lodash/noop'
import { useMachine } from '@xstate/react'

import { Season as SeasonLocation } from 'routing/routes'

import { WIPDataType } from 'model/WIP'
import { Season, SeasonCodec } from 'model/Season'
import { metadataMergeAutofill } from 'model/metadata/Metadata'

import { useUser } from 'auth/Auth'

import { fetchResource } from 'api/fetch'
import { metadataFetchAutofill } from 'api/autofill/metadataFetchAutofill'

import Loader from 'shared/components/Loader'
import Header from 'shared/components/Header'
import HeaderEditing from 'shared/components/Header/Editing'
import { ResourceMachineServiceContext } from 'shared/resource/ResourceMachineProvider'
import { createResourceMachine } from 'shared/resource/resource.machine'
import { useHasPortrayalsEditingRoles } from 'shared/hooks/useHasRole'
import { NotFoundError, ForbiddenError } from 'shared/errors/ServerError'

import SeasonNode from './SeasonNode'

const SeasonPage: React.FC = (): React.ReactElement => {
    const { seasonId } = useParams<SeasonLocation>()
    const handleError = useErrorHandler()

    const user = useUser()
    const canEditPortrayals = useHasPortrayalsEditingRoles()

    const wips: WIPDataType[] = useMemo(() => {
        return ['metadata', ...(canEditPortrayals ? ['characters' as WIPDataType] : [])]
    }, [canEditPortrayals])

    const [state, , service] = useMachine(
        () =>
            createResourceMachine<Season>({
                resourceId: seasonId,
                resourceType: 'season',
                user,
                wips,
                fetchFn: ctx => fetchResource({ codec: SeasonCodec, ...ctx }),
                handleCriticalError: (_, e: any) => handleError(e.data),
                // seasons cannot be locked, no need to pass a real refresh function
                refreshLockedTitles: noop,
                autofill: {
                    metadata: {
                        fetch: ctx => metadataFetchAutofill(ctx),
                        merge: metadataMergeAutofill,
                    },
                },
            }),
        {
            devTools: true,
        },
    )

    if (state.matches('loading')) {
        return (
            <>
                <Header />
                <Loader center size="normal" />
            </>
        )
    }

    if (state.matches('404')) {
        return (
            <>
                <Header />
                <NotFoundError />
            </>
        )
    }

    if (state.matches('403')) {
        return (
            <>
                <Header />
                <ForbiddenError />
            </>
        )
    }

    return (
        <ResourceMachineServiceContext.Provider value={service}>
            <HeaderEditing />
            <SeasonNode />
        </ResourceMachineServiceContext.Provider>
    )
}

export default SeasonPage
